import { useLocation, Link } from "react-router-dom";
import { BRAND } from "@/lib/constants";
import { MessageCircle, Phone, Mail } from "lucide-react";

/**
 * StickyContactBar — mobile-first sticky bottom bar with 3 always-visible actions:
 * WhatsApp • Call • Contact. On desktop (lg+), a single compact WhatsApp chip stays
 * bottom-right (unobtrusive). Replaces the previous two floating FABs.
 * All tap targets meet 44px minimum.
 */
export default function StickyContactBar() {
  const location = useLocation();

  // Hide on admin dashboards / login flows to avoid covering CTAs
  const hiddenOn = ["/admin", "/vendor", "/dashboard", "/login", "/register", "/forgot-password", "/reset-password"];
  if (hiddenOn.some((p) => location.pathname.startsWith(p))) return null;

  const waHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent("Hi EquipmentHub India, I'd like to know more.")}`;
  const telHref = `tel:+${BRAND.whatsappNumber}`;

  return (
    <>
      {/* Mobile: full-width bottom bar (3 actions) */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="sticky-contact-bar-mobile"
      >
        <div className="grid grid-cols-3 divide-x divide-slate-200">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] active:bg-slate-50 transition-colors"
            data-testid="sticky-bar-whatsapp"
            aria-label="Chat on WhatsApp"
          >
            <div className="w-9 h-9 rounded-full bg-[#25D366] grid place-items-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-[10px] font-bold text-slate-700 tracking-wide">WhatsApp</span>
          </a>
          <a
            href={telHref}
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] active:bg-slate-50 transition-colors"
            data-testid="sticky-bar-call"
            aria-label="Call EquipmentHub India"
          >
            <div className="w-9 h-9 rounded-full bg-ehi-primary grid place-items-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-[10px] font-bold text-slate-700 tracking-wide">Call Now</span>
          </a>
          <Link
            to="/contact"
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] active:bg-slate-50 transition-colors"
            data-testid="sticky-bar-contact"
            aria-label="Contact form"
          >
            <div className="w-9 h-9 rounded-full bg-ehi-accent grid place-items-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="text-[10px] font-bold text-slate-700 tracking-wide">Contact</span>
          </Link>
        </div>
      </div>

      {/* Desktop: compact WhatsApp chip only (bottom-right, non-intrusive) */}
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:inline-flex fixed bottom-6 right-6 z-50 items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white pl-3 pr-4 py-3 rounded-full shadow-2xl transition-all duration-200 hover:scale-105"
        data-testid="sticky-bar-whatsapp-desktop"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold whitespace-nowrap">Chat on WhatsApp</span>
      </a>
    </>
  );
}
